// Issue #359: Uploaded Test Data page (Hochgeladene Testdaten)
import React, { useState, useEffect, useCallback } from 'react';
import { betaApi } from '../../services/api';

function UploadedTestData() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({
    content_type: '',
    university: '',
  });

  // Available universities for beta testing
  const universities = ['TU Hamburg', 'HAW Kiel'];

  const perPage = 20;

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        page,
        per_page: perPage,
        ...(filters.content_type && { content_type: filters.content_type }),
        ...(filters.university && { university: filters.university }),
      };
      const response = await betaApi.listUploaded(params);
      setItems(response.data.items || []);
      setTotal(response.data.total || 0);
    } catch (err) {
      console.error('Error fetching uploaded test data:', err);
      setError('Fehler beim Laden der Testdaten');
    } finally {
      setLoading(false);
    }
  }, [page, filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getContentTypeLabel = (type) => {
    const labels = {
      post: 'Beitrag',
      marketplace: 'Marktplatz',
      comment: 'Kommentar',
      course_review: 'Kursbewertung',
    };
    return labels[type] || type;
  };

  const truncateContent = (content, maxLength = 100) => {
    if (!content) return '-';
    if (content.length <= maxLength) return content;
    return content.substring(0, maxLength) + '...';
  };

  const totalPages = Math.ceil(total / perPage);

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1>Hochgeladene Testdaten</h1>
          <p className="page-subtitle">{total} Einträge insgesamt</p>
        </div>
      </div>

      <div className="filters-bar">
        <select
          className="filter-select"
          value={filters.content_type}
          onChange={(e) => {
            setFilters({ ...filters, content_type: e.target.value });
            setPage(1);
          }}
        >
          <option value="">Alle Inhaltstypen</option>
          <option value="post">Beitrag</option>
          <option value="marketplace">Marktplatz</option>
          <option value="comment">Kommentar</option>
          <option value="course_review">Kursbewertung</option>
        </select>

        <select
          className="filter-select"
          value={filters.university}
          onChange={(e) => {
            setFilters({ ...filters, university: e.target.value });
            setPage(1);
          }}
        >
          <option value="">Alle Universitäten</option>
          {universities.map((uni) => (
            <option key={uni} value={uni}>{uni}</option>
          ))}
        </select>
      </div>

      {error && <div className="error-message">{error}</div>}

      {loading ? (
        <div className="loading-container">
          <div className="loading-spinner"></div>
        </div>
      ) : items.length === 0 ? (
        <div className="empty-state">
          <p>Keine hochgeladenen Testdaten vorhanden.</p>
          <p>Testdaten werden automatisch hochgeladen, wenn die geplante Zeit erreicht ist.</p>
        </div>
      ) : (
        <>
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Typ</th>
                  <th>Universität</th>
                  <th>Inhalt</th>
                  <th>Geplante Zeit</th>
                  <th>Hochgeladen am</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id}>
                    <td>
                      <span className={`type-badge ${item.content_type}`}>
                        {getContentTypeLabel(item.content_type)}
                      </span>
                    </td>
                    <td>{item.university}</td>
                    <td className="content-cell">
                      {item.title && <strong>{item.title}: </strong>}
                      {truncateContent(item.content)}
                    </td>
                    <td>{formatDate(item.scheduled_at)}</td>
                    <td>{formatDate(item.uploaded_at)}</td>
                    <td>
                      <span className={`status-badge ${item.status === 'uploaded' ? 'verified' : 'unverified'}`}>
                        {item.status === 'uploaded' ? 'Hochgeladen' : item.status === 'failed' ? 'Fehlgeschlagen' : item.status}
                      </span>
                      {item.uploaded_content_id && (
                        <span style={{ display: 'block', fontSize: '11px', color: '#666', marginTop: '4px' }} title={item.uploaded_content_id}>
                          ID: {item.uploaded_content_id.substring(0, 8)}...
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="pagination">
              <button
                className="pagination-btn"
                disabled={page === 1}
                onClick={() => setPage(page - 1)}
              >
                ← Zurück
              </button>
              <span className="pagination-info">
                Seite {page} von {totalPages}
              </span>
              <button
                className="pagination-btn"
                disabled={page === totalPages}
                onClick={() => setPage(page + 1)}
              >
                Weiter →
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default UploadedTestData;
